// src/pages/UploadPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import CachedIcon from '@mui/icons-material/Cached';
import ApiService from '../services/api';
import ProgressTracker from '../components/upload/ProgressTracker';
import { calculateFileHash, storeFileHash } from '../utils/fileHash';

const UploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisMessage, setAnalysisMessage] = useState('');
  const [currentAnalysisStep, setCurrentAnalysisStep] = useState('');
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [forceReanalysis, setForceReanalysis] = useState(false);
  const [fileHash, setFileHash] = useState(null);
  const [useCachedVersion, setUseCachedVersion] = useState(true);
  const [calculatingHash, setCalculatingHash] = useState(false);

  const steps = ['Select File', 'Upload', 'Analysis'];
  
  // Check for messages from navigation (e.g., on redirect)
  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
    }
  }, [location]);
  
  // Automatic redirect when analysis is completed
  useEffect(() => {
    // Only redirect when:
    // 1. analysisCompleted is true
    // 2. taskId is available
    // 3. progress has actually reached 100%
    if (analysisCompleted && taskId && analysisProgress >= 100) {
      console.log("Completion conditions met, navigating shortly...");
      
      // Short delay so the user can see that the analysis is completed
      const timer = setTimeout(() => {
        console.log(`Analysis completed, navigating to dashboard for task: ${taskId}`);
        navigate(`/dashboard/${taskId}`);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [analysisCompleted, taskId, navigate, analysisProgress]);
  
  // Determine current analysis step based on message
  useEffect(() => {
    if (!analysisMessage) return;
    
    console.log("Analysis message updated:", analysisMessage);
    
    // If the message contains something about cache, ensure we're on summary step
    if (analysisMessage.toLowerCase().includes('cache')) {
      console.log("Cached analysis detected");
      setCurrentAnalysisStep('summary');
    }
    
    // Progress-based step tracking
    if (analysisProgress > 90) {
      setCurrentAnalysisStep('summary');
    } else if (analysisProgress > 83) {
      setCurrentAnalysisStep('strategic_finance');
    } else if (analysisProgress > 75) {
      setCurrentAnalysisStep('finance');
    } else if (analysisProgress > 65) {
      setCurrentAnalysisStep('market');
    } else if (analysisProgress > 52) {
      setCurrentAnalysisStep('product');
    }
    
    // Message-based step tracking as backup
    const lowerMsg = analysisMessage.toLowerCase();
    let nextStep = null;
    
    if (lowerMsg.includes('ocr')) {
      nextStep = 'ocr';
    } else if (lowerMsg.includes('completeness')) {
      nextStep = 'completeness';
    } else if (lowerMsg.includes('elevator pitch')) {
      nextStep = 'elevator_pitch';
    } else if (lowerMsg.includes('team')) {
      nextStep = 'team';
    } else if (lowerMsg.includes('product')) {
      nextStep = 'product';
    } else if (lowerMsg.includes('market')) {
      nextStep = 'market';
    } else if ((lowerMsg.includes('finance') || lowerMsg.includes('financial')) 
              && !(lowerMsg.includes('strategic'))) {
      nextStep = 'finance';
    } else if ((lowerMsg.includes('strategic') && lowerMsg.includes('finance'))) {
      nextStep = 'strategic_finance';
    } else if (lowerMsg.includes('summary')) {
      nextStep = 'summary';
    }
    
    if (nextStep) {
      console.log(`Setting current step to: ${nextStep} based on message`);
      setCurrentAnalysisStep(nextStep);
    }
    
    // Check for completion message - but only with 100% progress!
    if (
      analysisProgress >= 100 &&
      (lowerMsg.includes('completed') || 
       lowerMsg.includes('analysis complete'))
    ) {
      console.log('Analysis completion message detected with 100% progress');
      setAnalysisCompleted(true);
    } else {
      // If the message suggests completion but progress isn't 100%,
      // something is wrong - explicitly set to not completed
      if (lowerMsg.includes('completed') || lowerMsg.includes('analysis complete')) {
        console.log(`Completion message detected but progress is only ${analysisProgress}% - ignoring`);
        setAnalysisCompleted(false);
      }
    }
  }, [analysisMessage, analysisProgress]);

  // Automatic step update based on progress
  useEffect(() => {
    // Mapping of progress ranges to steps
    const progressSteps = [
      { min: 0, max: 12, step: 'ocr' },
      { min: 13, max: 25, step: 'completeness' },
      { min: 26, max: 38, step: 'elevator_pitch' },
      { min: 39, max: 51, step: 'team' },
      { min: 52, max: 64, step: 'product' },
      { min: 65, max: 77, step: 'market' },
      { min: 78, max: 83, step: 'finance' },
      { min: 84, max: 90, step: 'strategic_finance' },
      { min: 91, max: 100, step: 'summary' }
    ];
    
    // Find the current step based on progress
    const matchedStep = progressSteps.find(
      s => analysisProgress >= s.min && analysisProgress <= s.max
    );
    
    if (matchedStep && matchedStep.step !== currentAnalysisStep) {
      console.log(`Setting step to ${matchedStep.step} based on progress ${analysisProgress}%`);
      setCurrentAnalysisStep(matchedStep.step);
    }
    
    // Only mark as completed when progress is actually 100%
    if (analysisProgress >= 100) {
      console.log("Progress reached 100%, analysis can be marked as completed now");
      // The analysisCompleted flag is only set via message detection
    } else if (analysisProgress < 95) {
      // If progress is below 95%, the analysis cannot be completed
      if (analysisCompleted) {
        console.log(`Progress is only ${analysisProgress}%, resetting completed flag`);
        setAnalysisCompleted(false);
      }
    }
  }, [analysisProgress, currentAnalysisStep, analysisCompleted]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setActiveStep(1);
      setError(null);
      
      // Calculate file hash and check backend cache
      try {
        setCalculatingHash(true);
        const hash = await calculateFileHash(selectedFile);
        console.log("Calculated file hash:", hash);
        
        // Check backend cache directly
        const cacheStatus = await ApiService.checkCache(hash);
        if (cacheStatus.cached) {
          console.log("Found cached analysis:", cacheStatus.task_id);
        }
        
        setFileHash(hash);
        setCalculatingHash(false);
      } catch (error) {
        console.error("Error calculating hash or checking cache:", error);
        setCalculatingHash(false);
      }
    } else {
      setFile(null);
      setError('Please select a valid PDF file.');
    }
  };

  // Function for reliable status monitoring
  const monitorAnalysisStatus = async (taskId) => {
    // Start with clear status
    setAnalysisProgress(0);
    setAnalysisMessage('Initializing analysis...');
    setCurrentAnalysisStep('ocr');
    setLastFetchTime(Date.now());
    setAnalysisCompleted(false);

    console.log(`Starting status monitoring for task: ${taskId}`);
    
    // Define polling function
    const checkStatus = async () => {
      try {
        const now = Date.now();
        setLastFetchTime(now);
        
        const statusResponse = await ApiService.getAnalysisStatus(taskId);
        
        // Extract values from the response
        const responseProgress = statusResponse?.progress;
        const responseMessage = statusResponse?.message || '';
        const responseStatus = statusResponse?.status || '';
        
        console.log(`Status response: progress=${responseProgress}, status=${responseStatus}, message=${responseMessage}`);
        
        // Update progress only if it's a valid number
        if (responseProgress !== undefined && responseProgress !== null) {
          const numericProgress = typeof responseProgress === 'string' 
            ? parseInt(responseProgress, 10) 
            : Number(responseProgress);
          
          if (!isNaN(numericProgress)) {
            // Avoid backward progress
            if (numericProgress >= analysisProgress) {
              setAnalysisProgress(numericProgress);
              console.log(`Progress updated: ${numericProgress}%`);
            }
          }
        }
        
        // Update message if present
        if (responseMessage) {
          setAnalysisMessage(responseMessage);
        }
        
        // Check if analysis is complete or has an error
        if (responseStatus === 'completed' || responseStatus === 'Analysis loaded from cache') {
          // Only mark as completed when progress is actually 100%
          if (responseProgress >= 100) {
            console.log('Analysis completed with 100% progress! Setting final status.');
            
            // Make sure we set to 100% and mark all steps as completed
            setAnalysisProgress(100);
            setCurrentAnalysisStep('summary');
            setAnalysisMessage(responseMessage || 'Analysis completed');
            setAnalysisCompleted(true);
            
            return true; // Signal to stop polling
          } else {
            console.log(`Received 'completed' status but progress is only ${responseProgress}%. Continuing to monitor...`);
            // We set the progress a bit higher, but not to 100%
            setAnalysisProgress(Math.max(analysisProgress, 95));
            // We continue polling
          }
        } else if (responseStatus === 'error') {
          console.error(`Error during analysis: ${responseMessage}`);
          setError(`Error during analysis: ${responseMessage}`);
          setLoading(false);
          return true; // Signal to stop polling
        }
        
        // Progress estimation when no updates for too long
        const timeSinceLastUpdate = Date.now() - now;
        if (timeSinceLastUpdate > 10000 && analysisProgress > 0) {
          // If 10 seconds have passed without updates and we are at certain steps,
          // we increase the progress slightly to simulate progress
          const knownStagnationPoints = ['market', 'finance', 'strategic_finance'];
          if (knownStagnationPoints.includes(currentAnalysisStep)) {
            const nextStep = knownStagnationPoints[knownStagnationPoints.indexOf(currentAnalysisStep) + 1];
            if (nextStep) {
              console.log(`No updates for a while, advancing to next step: ${nextStep}`);
              setCurrentAnalysisStep(nextStep);
              const progressIncrement = 5;
              setAnalysisProgress(prev => Math.min(prev + progressIncrement, 95)); // Never set to 100%
            }
          }
        }
        
        // Continue polling
        return false;
        
      } catch (error) {
        console.error("Error fetching status:", error);
        // Don't stop polling on error, might be temporary
        return false;
      }
    };
    
    // Initial check, then regular polling
    const shouldStopPolling = await checkStatus();
    if (!shouldStopPolling) {
      const intervalId = setInterval(async () => {
        const shouldStop = await checkStatus();
        if (shouldStop) {
          clearInterval(intervalId);
          console.log('Status polling ended.');
        }
      }, 2000); // Check every 2 seconds
      
      // Return cleanup function for useEffect
      return () => {
        clearInterval(intervalId);
        console.log('Status polling stopped by cleanup.');
      };
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisCompleted(false);

    try {
      // First check if we can use cached results
      if (useCachedVersion && fileHash && !forceReanalysis) {
        const cacheStatus = await ApiService.checkCache(fileHash);
        if (cacheStatus.cached) {
          console.log("Using cached analysis:", cacheStatus.task_id);
          setTaskId(cacheStatus.task_id);
          setAnalysisProgress(100); // Set to 100% for cached results
          setAnalysisMessage("Analysis loaded from cache");
          setCurrentAnalysisStep('summary');
          setAnalysisCompleted(true); // Mark as completed
          setActiveStep(2);
          
          // Skip results loading - will be fetched when navigating to dashboard
          return;
        }
      }

      // If no cached version available, proceed with upload and analysis
      console.log('Uploading file...');
      const uploadResponse = await ApiService.uploadPDF(file);
      const fileId = uploadResponse.file_id;
      console.log(`File uploaded with ID: ${fileId}`);

      // Start analysis with cache info and original filename
      console.log('Starting analysis...');
      const analysisResponse = await ApiService.startAnalysis(
        fileId, 
        { // Options object
          useCache: useCachedVersion && !forceReanalysis,
          fileHash: fileHash
        },
        file.name // Pass the original filename here
      );
      
      const newTaskId = analysisResponse.task_id;
      setTaskId(newTaskId);
      console.log(`Analysis started with task ID: ${newTaskId}`);
      setActiveStep(2);
      
      // If status is already "loaded from cache", set high progress
      if (analysisResponse.status === "Analysis loaded from cache") {
        console.log("Analysis loaded from cache, setting high progress");
        setAnalysisProgress(95);
        setAnalysisMessage("Analysis loaded from cache");
        setCurrentAnalysisStep('summary');
      }
      
      // Start monitoring the status
      monitorAnalysisStatus(newTaskId);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError(`Error uploading or starting the analysis: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', my: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Upload Pitch Deck
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        {activeStep === 0 && (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              py: 4,
              borderRadius: 1,
              border: '2px dashed #ccc',
              backgroundColor: '#fafafa',
            }}
          >
            <input
              type="file"
              id="file-upload"
              accept="application/pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Drag your pitch deck here or
            </Typography>
            <Button 
              variant="contained" 
              component="label" 
              htmlFor="file-upload"
              disabled={calculatingHash}
            >
              {calculatingHash ? <CircularProgress size={24} /> : "Browse"}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Supported formats: PDF
            </Typography>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Selected file: {file?.name}
            </Typography>
            
            {/* Cache Option */}
            {fileHash && (
              <Box sx={{ my: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={useCachedVersion} 
                      onChange={(e) => setUseCachedVersion(e.target.checked)}
                    />
                  }
                  label="Use cached analysis results if available"
                />
                <Tooltip title="Determines whether to use a previously analyzed version of this file (faster) or to analyze the file anew.">
                  <IconButton size="small">
                    <CachedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            
            <Button 
              variant="contained" 
              onClick={handleUpload} 
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? 'Uploading...' : 'Upload and Start Analysis'}
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => setActiveStep(0)} 
              sx={{ mt: 2, ml: 2 }}
              disabled={loading}
            >
              Back
            </Button>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Analysis in progress...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              (May take up to 10 minutes)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
              {/* Use the ProgressTracker component - only pass completed when analysisProgress is 100% */}
              <ProgressTracker 
                progress={analysisProgress} 
                message={analysisMessage} 
                currentStep={currentAnalysisStep} 
                completed={analysisCompleted && analysisProgress >= 100}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UploadPage;
